<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-horizontal="start"
            align-vertical="end"
        >
            <DCol lg="2">
                <label class="form-label">{{ __('tools.invoice_uploads.index.table.status') }}</label>
                <DMultiSelect
                    :options="statusOptions"
                    selection-type="tags"
                    :multiple="true"
                    :selection="true"
                    :selected="selectedStatuses"
                    inline
                    @update="updateSelectedStatus"
                />
            </DCol>
            <DCol
                lg="3"
                class="d-flex gap-2"
            >
                <DButton
                    id="filter-apply"
                    :label="__('messages.actions.filter')"
                    @click="changePage"
                />
                <DButton
                    id="filter-reset"
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DRow>
            <DCol>
                <DTable
                    :key="tableKey"
                    :items="data"
                    :fields="fields"
                    hover
                >
                    <template #id="{item}">
                        <td>
                            <Link :href="manualInvoiceUploadRoute(item.id)">
                                {{ item.id }}
                            </Link>
                        </td>
                    </template>
                    <template #status="{item}">
                        <td>
                            <DTag
                                :label="item.status"
                                :color="getStatusTagColor(item.status)"
                            />
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td>
                            <DButtonDropdown
                                icon="dots-horizontal"
                            >
                                <DDropdownItem
                                    icon="eye"
                                    @click="show(item.id)"
                                >
                                    {{ __('tools.invoice_uploads.index.preview') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    icon="download"
                                    @click="downloadFile(item.id)"
                                >
                                    {{ __('tools.invoice_uploads.index.download') }}
                                </DDropdownItem>
                                <DDropdownItem
                                    v-if="item.status === 'open'"
                                    icon="close"
                                    @click="cancelInvoices(item.id)"
                                >
                                    {{ __('tools.invoice_uploads.index.cancel') }}
                                </DDropdownItem>
                            </DButtonDropdown>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="files.last_page > 1"
                    :active-page="files.current_page"
                    :pages="files.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
        <DFabs>
            <DFab
                id="btn-preview-new-pricing"
                icon="plus"
                :label="__('tools.invoice_uploads.index.upload.button')"
                @click="upload"
            />
        </DFabs>
        <DModal
            v-if="showDeleteConfirmationModal"
            v-model:show="showDeleteConfirmationModal"
            :title="__('tools.invoice_uploads.cancel.modal_title')"
            :message="__('tools.invoice_uploads.cancel.modal_text')"
            is-confirm
            is-error
            @close="closeDeleteModal"
            @confirm="cancelUpload"
        />
    </DCard>
</template>

<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import { computed, onMounted, ref } from 'vue'

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    files: {
        type: Object,
        default: () => {
            return {}
        }
    },
    statuses: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const tableKey = ref(0)
const data = ref([])

onMounted(() => {
    window.laravelEcho.channel('onecrm.manual_invoices.upload').listen('.manual_invoices.upload_status_updated', socketEvent => {
        handleStatusUpdate(socketEvent)
    })
    data.value = props.items
})

const fields = [
    { key: 'id', label: __('tools.invoice_uploads.index.table.id'), _style: { minWidth: '10%' } },
    { key: 'user', label: __('tools.invoice_uploads.index.table.user') },
    { key: 'date', label: __('tools.invoice_uploads.index.table.date') },
    { key: 'records', label: __('tools.invoice_uploads.index.table.records') },
    { key: 'filename', label: __('tools.invoice_uploads.index.table.filename') },
    { key: 'status', label: __('tools.invoice_uploads.index.table.status') },
    { key: 'notInvoiced', label: __('tools.invoice_uploads.index.table.not_invoiced') },
    { key: 'actions', label: '', _style: { minWidth: '5%' } }
]

// Cancel upload
const showDeleteConfirmationModal = ref(false)
const closeDeleteModal = () => {
    itemToBeCanceled.value = null
    showDeleteConfirmationModal.value = false
}
const dataFilters = ref({
    statuses: (props.filters !== null && props.filters.statuses) || getParamArray('statuses')
})

const itemToBeCanceled = ref(null)

function getParamArray (paramName) {
    const params = new URLSearchParams(window.location.search)
    const paramArray = []
    const expression = new RegExp('^' + paramName + '[\\[]\\d*[\\]]$')
    for (const param of params.entries()) {
        if (param[0].match(expression)) {
            paramArray.push(param[1])
        }
    }
    return paramArray
}

const upload = () => router.get(route('tools.manual-invoice-uploads.upload'))
const show = (id) => router.get(route('tools.manual-invoice-uploads.show', id))

const changePage = (page) => {
    const queryParameters = Object.fromEntries(Object.entries(dataFilters.value).filter(([_, v]) => v))
    if (page > 1) {
        queryParameters.page = page
    }

    router.get(route('tools.manual-invoice-uploads.index', {
        saveFilters: true
    }), queryParameters)
}

const downloadFile = (id) => {
    const link = document.createElement('a')
    link.href = route('tools.manual-invoice-uploads.download-file', id)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const resetFilters = () => {
    router.get(route('tools.manual-invoice-uploads.index', {
        saveFilters: true,
        defaultFilters: true
    }))
}

const updateSelectedStatus = (values) => {
    dataFilters.value.statuses = values
}

const selectedStatuses = () => {
    if (!dataFilters.value.statuses) return []
    return dataFilters.value.statuses.map((item) => item)
}

const cancelUpload = () => {
    router.post(route('tools.manual-invoice-uploads.cancel', {
        manualInvoiceUpload: itemToBeCanceled.value
    }))
}

const statusOptions = computed(() => {
    return [
        { value: 'open', text: __('tools.invoice_uploads.statuses.open'), selected: selectedStatuses().includes('open') },
        { value: 'pending', text: __('tools.invoice_uploads.statuses.pending'), selected: selectedStatuses().includes('pending') },
        { value: 'done', text: __('tools.invoice_uploads.statuses.done'), selected: selectedStatuses().includes('done') },
        { value: 'canceled', text: __('tools.invoice_uploads.statuses.canceled'), selected: selectedStatuses().includes('canceled') }
    ]
})

const cancelInvoices = (id) => {
    itemToBeCanceled.value = id
    showDeleteConfirmationModal.value = true
}

const manualInvoiceUploadRoute = (id) => route('tools.manual-invoice-uploads.show', {
    manualInvoiceUpload: id
})

const getStatusTagColor = (status) => {
    if (status === 'open') {
        return 'primary'
    }
    if (status === 'done') {
        return 'success'
    }
    if (status === 'pending') {
        return 'secondary'
    }
    return 'danger'
}

const handleStatusUpdate = (socketEvent) => {
    // do something only if event came from same tenant
    if (socketEvent.tenant !== usePage().props.tenant) {
        return
    }

    const manualInvoiceUpload = socketEvent.manualInvoiceUpload
    const existingRequestId = data.value.findIndex(existingRequest => existingRequest.id === manualInvoiceUpload.id)

    // remove highlight-row class from selected row after 3000ms
    setTimeout(() => {
        manualInvoiceUpload._classes = ''
        data.value.splice(existingRequestId, 1, manualInvoiceUpload)
        tableKey.value++
    }, 3000)

    manualInvoiceUpload._classes = manualInvoiceUpload.status === 'failed' ? 'highlight-row' : 'highlight-row-success'
    data.value.splice(existingRequestId, 1, manualInvoiceUpload)
}
</script>

<style scoped lang="scss">

</style>
